/* eslint-disable @next/next/no-img-element */
import * as React from 'react'
import {
  Repository,
  Team,
  READMEScreenshots as TREADMEScreenshots,
} from '@basehub/data/replicache/index'
import {
  PublicRepo,
  PublicTeam,
} from '@basehub/data/replicache/mutators/_public-types'
import {
  VALID_IMAGE_MIME_TYPES,
  VALID_VIDEO_MIME_TYPES,
} from '@basehub/data/replicache/schema/block/valid-mime-types'
import { FullBleedContainer } from '../../../../_components/full-bleed-container'
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  Cross2Icon,
  DragHandleDots2Icon,
  PlusIcon,
} from '@radix-ui/react-icons'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import { UndoManager } from '@rocicorp/undo'
import { isHotkey } from 'is-hotkey'
import { getUploadSignedUrl } from './_actions'
import { useMutation } from '@tanstack/react-query'
import { Replicache } from 'replicache'
import { TeamSpaceMutators } from '@basehub/data/replicache/mutators/team-space/mutators'
import { getId } from '@basehub/utils/id/index'
import { clsx } from 'clsx'
import { IconButton } from '~/sotano-kit/primitives/icon-button'
import { useSyncedValue } from '~/hooks/use-synced-value'
import { Button } from '~/sotano-kit/primitives/button'
import { getFileHashSHA256 } from '@basehub/data/blob/file-utils'

const DND_ENABLED = false

export const READMEScreenshots = ({
  repo,
  team,
  visitor,
}: {
  repo: Repository | PublicRepo
  team: Team | PublicTeam
  visitor?: boolean
}) => {
  const screenshots = repo.readme?.screenshots ?? []

  if (visitor) {
    if (screenshots.length === 0) return null
    return (
      <div className="space-y-6">
        <FullBleedContainer initialOverflow="visible">
          <div className="flex h-80 gap-x-6">
            <Preview screenshots={screenshots} />
          </div>
        </FullBleedContainer>
      </div>
    )
  }

  return (
    <div className="space-y-10 h-[204px]">
      <FullBleedContainer>
        <Editor repo={repo} team={team} screenshots={screenshots} />
      </FullBleedContainer>
    </div>
  )
}

const EditorContext = React.createContext<{
  repo: Repository | PublicRepo
  team: Team | PublicTeam
  teamReplicache: Replicache<TeamSpaceMutators> | undefined
  undoManager: UndoManager
  screenshotsRef: React.RefObject<TREADMEScreenshots | undefined>
} | null>(null)

const useEditorContext = () => {
  const context = React.useContext(EditorContext)
  if (!context) {
    throw new Error(
      'useEditorContext must be used within an EditorContext.Provider'
    )
  }
  return context
}

const Editor = ({
  repo,
  team,
  screenshots,
}: {
  repo: Repository | PublicRepo
  team: Team | PublicTeam
  screenshots: TREADMEScreenshots
}) => {
  const rr = useRepoReplicache()
  const teamReplicache = rr.data?.teamQueryData?.replicache

  const [undoManager] = React.useState(new UndoManager())

  const screenshotsRef = React.useRef(repo.readme?.screenshots ?? [])
  // eslint-disable-next-line react-compiler/react-compiler
  screenshotsRef.current = repo.readme?.screenshots ?? []

  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isHotkey('mod+z', e)) {
        undoManager.undo()
      } else if (isHotkey('mod+shift+z', e)) {
        undoManager.redo()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [undoManager])

  return (
    <EditorContext.Provider
      value={{ repo, team, teamReplicache, undoManager, screenshotsRef }}
    >
      <div className="flex gap-x-4">
        {screenshots.map((screenshot, i) => {
          return <Screenshot key={screenshot.id} data={screenshot} index={i} />
        })}

        <UploadFile />
      </div>
    </EditorContext.Provider>
  )
}

type TScreenshot = TREADMEScreenshots[number]

const Screenshot = ({ data, index }: { data: TScreenshot; index: number }) => {
  const [isFocusingAltText, setIsFocusingAltText] = React.useState(false)
  const { repo, teamReplicache, undoManager, screenshotsRef } =
    useEditorContext()

  const handleRemove = React.useCallback(async () => {
    const currentScreenshots = screenshotsRef.current ?? []
    const newScreenshots = currentScreenshots.filter((_, j) => j !== index)

    undoManager.add({
      execute: async () => {
        await teamReplicache?.mutate.updateScreenshotsREADME({
          repoSlug: repo.slug,
          repoId: repo.id,
          screenshots: newScreenshots,
        })
      },
      undo: async () => {
        await teamReplicache?.mutate.updateScreenshotsREADME({
          repoSlug: repo.slug,
          repoId: repo.id,
          screenshots: currentScreenshots,
        })
      },
    })
  }, [
    screenshotsRef,
    repo.slug,
    repo.id,
    undoManager,
    index,
    teamReplicache?.mutate,
  ])

  const handleReorder = React.useCallback(
    async (direction: 'right' | 'left') => {
      const currentScreenshots = screenshotsRef.current ?? []

      let desiredPosition
      let currentInDesiredPosition
      const current = currentScreenshots[index]

      if (!current) return

      if (direction === 'left') {
        currentInDesiredPosition = currentScreenshots[index - 1]
        desiredPosition = index - 1
      } else {
        currentInDesiredPosition = currentScreenshots[index + 1]
        desiredPosition = index + 1
      }

      if (
        desiredPosition < 0 ||
        desiredPosition >= currentScreenshots.length ||
        !currentInDesiredPosition
      ) {
        return
      }

      const currentScreenshotsCopy = [...currentScreenshots]
      currentScreenshotsCopy[index] = currentInDesiredPosition
      currentScreenshotsCopy[desiredPosition] = current

      undoManager.add({
        execute: async () => {
          await teamReplicache?.mutate.updateScreenshotsREADME({
            repoSlug: repo.slug,
            repoId: repo.id,
            screenshots: currentScreenshotsCopy,
          })
        },
        undo: async () => {
          await teamReplicache?.mutate.updateScreenshotsREADME({
            repoSlug: repo.slug,
            repoId: repo.id,
            screenshots: currentScreenshots,
          })
        },
      })
    },
    [
      screenshotsRef,
      repo.slug,
      repo.id,
      index,
      undoManager,
      teamReplicache?.mutate,
    ]
  )

  const setAltText = React.useCallback(
    async (value: string) => {
      const currentScreenshots = screenshotsRef?.current ?? []

      await teamReplicache?.mutate.updateScreenshotsREADME({
        repoSlug: repo.slug,
        repoId: repo.id,
        screenshots: currentScreenshots.map((screenshot) => {
          if (data.id === screenshot.id) {
            return { ...screenshot, alt: value }
          }

          return screenshot
        }),
      })
    },
    [data.id, repo.id, repo.slug, screenshotsRef, teamReplicache?.mutate]
  )

  const { value, ref, onChange } = useSyncedValue<HTMLInputElement>({
    value: data.alt ?? '',
    setValue: setAltText,
    enabled: true,
  })

  return (
    <div className="relative">
      <div className="flex-grow shrink-0 flex items-center gap-2.5 relative group h-[200px]">
        <div
          onClick={() => {
            ref.current?.select()
          }}
          className={clsx(
            'group-hover:opacity-20',
            isFocusingAltText && 'opacity-20'
          )}
        >
          <ImageOrVideo
            src={data.url}
            mimeType={data.mimeType}
            alt={data.alt}
          />
        </div>

        <div
          className={clsx(
            !isFocusingAltText && 'invisible group-hover:visible',
            'bg-gradient-to-t h-20 from-dark-bg to-transparent bottom-px left-px right-px rounded-b-[7px] absolute'
          )}
        />

        <div className="gap-2 flex items-center absolute top-2 right-2">
          {/* remove */}
          <IconButton
            className="bg-dark-shade border-dark-border-solid border"
            onMouseEnter={() => setIsFocusingAltText(true)}
            onMouseLeave={() => setIsFocusingAltText(false)}
            onClick={() => {
              handleRemove()
            }}
          >
            <Cross2Icon />
          </IconButton>
          {DND_ENABLED && (
            <IconButton className="text-dark-muted">
              <DragHandleDots2Icon />
            </IconButton>
          )}
        </div>

        <div
          className={clsx(
            !isFocusingAltText && 'invisible group-hover:visible',
            'absolute bottom-2 flex gap-x-2 justify-between inset-x-2'
          )}
        >
          {/* left */}
          <IconButton
            className="bg-dark-shade border-dark-border-solid border"
            onClick={() => {
              handleReorder('left')
            }}
          >
            <ArrowLeftIcon />
          </IconButton>

          {/* alt text */}
          <input
            ref={ref}
            value={value}
            type="text"
            name="alt"
            placeholder="Alt text"
            autoComplete="off"
            className="text-dark-bright text-sm mr-2 w-4/5 bottom-2 left-1/2 bg-transparent border-b border-dark-muted placeholder:text-dark-muted focus:outline-none focus:border-dark-dim"
            onFocus={() => setIsFocusingAltText(true)}
            onBlur={() => setIsFocusingAltText(false)}
            onKeyDown={(e) => {
              const input = e.target as HTMLInputElement | null
              if (!input) return
              if (e.key === 'Enter') input.blur()
              if (e.key === 'Esc') input.blur()
            }}
            onChange={(e) => {
              onChange(e.target.value)
            }}
          />

          {/* right */}
          <IconButton
            className="bg-dark-shade border-dark-border-solid border"
            onClick={() => {
              handleReorder('right')
            }}
          >
            <ArrowRightIcon />
          </IconButton>
        </div>
      </div>
    </div>
  )
}

const ImageOrVideo = ({
  src,
  alt,
  mimeType,
}: {
  src: string
  alt?: string
  mimeType: string
}) => {
  if (VALID_IMAGE_MIME_TYPES.includes(mimeType)) {
    return (
      <img
        src={src}
        alt={alt}
        className="rounded-lg border border-dark-border-solid object-cover w-[300px] min-w-10 h-[200px]"
      />
    )
  } else if (VALID_VIDEO_MIME_TYPES.includes(mimeType)) {
    return (
      <video
        src={src}
        className="rounded-lg border border-dark-border-solid object-cover w-[300px] min-w-10 h-[200px]"
        loop
        autoPlay
        muted
        playsInline
      />
    )
  }
}

const UploadFile = () => {
  const { repo, team, teamReplicache, undoManager, screenshotsRef } =
    useEditorContext()
  const inputRef = React.useRef<HTMLInputElement>(null)
  const [uploadQueue, setUploadQueue] = React.useState<Array<{
    id: string
    src: string
    mimeType: string
  }> | null>(null)
  const uploadQueueRef = React.useRef(uploadQueue)
  // eslint-disable-next-line react-compiler/react-compiler
  uploadQueueRef.current = uploadQueue

  const [isDroppingImage, setIsDroppingImage] = React.useState(false)

  const { mutateAsync: upload } = useMutation(
    async ({ file }: { file: File }) => {
      const { signedURL, uploadURL } = await getUploadSignedUrl({
        teamId: team.id,
        repoId: repo.id,
        requestedSize: file.size,
        requestedType: file.type,
        fileName: file.name,
        fileHash: await getFileHashSHA256(file),
      })
      await fetch(signedURL, {
        method: 'PUT',
        body: file,
      })
      return { uploadURL }
    }
  )

  const handleUpload = React.useCallback(
    async (file: File) => {
      const type = VALID_IMAGE_MIME_TYPES.includes(file.type) ? 'img' : 'video'
      const imageOrVideo = document.createElement(type)

      const id = getId()

      imageOrVideo.addEventListener(
        type === 'img' ? 'load' : 'loadedmetadata',
        async () => {
          const { uploadURL } = await upload({ file })
          const isRemoved =
            uploadQueueRef.current?.find((item) => item.id === id) === undefined

          if (isRemoved) return

          setUploadQueue((p) => {
            if (!p) return p
            return p.filter((item) => item.id !== id)
          })

          // because this fn can be called concurrently, we get the state from a ref at this moment
          const currentScreenshots = screenshotsRef.current ?? []

          await undoManager.add({
            execute: async () => {
              await teamReplicache?.mutate.updateScreenshotsREADME({
                repoSlug: repo.slug,
                repoId: repo.id,
                screenshots: [
                  ...currentScreenshots,
                  {
                    id,
                    url: uploadURL,
                    width:
                      type === 'img'
                        ? imageOrVideo.width
                        : (imageOrVideo as HTMLVideoElement).videoWidth,
                    height:
                      type === 'img'
                        ? imageOrVideo.height
                        : (imageOrVideo as HTMLVideoElement).videoHeight,
                    alt: '',
                    mimeType: file.type,
                  },
                ],
              })
            },
            undo: async () => {
              await teamReplicache?.mutate.updateScreenshotsREADME({
                repoSlug: repo.slug,
                repoId: repo.id,
                screenshots: currentScreenshots,
              })
            },
          })
        }
      )

      const previewUrl = URL.createObjectURL(file)
      imageOrVideo.src = previewUrl

      setUploadQueue((p) => {
        if (!p) return [{ id, src: previewUrl, mimeType: file.type }]
        return [...p, { id, src: previewUrl, mimeType: file.type }]
      })
    },
    [
      repo.id,
      repo.slug,
      screenshotsRef,
      teamReplicache?.mutate,
      undoManager,
      upload,
    ]
  )

  return (
    <>
      {uploadQueue?.map((preview) => {
        return (
          <div
            key={preview.id}
            className="flex items-center gap-4 shrink-0 w-[300px] h-[200px] cursor-default select-none text-sm text-dark-muted relative"
          >
            <div className="opacity-20">
              <ImageOrVideo
                src={preview.src}
                mimeType={preview.mimeType}
              />{' '}
            </div>
            <span className="absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2">
              Uploading...
            </span>

            {/* remove from upload queue */}
            <IconButton
              className="bg-dark-shade border-dark-border-solid border top-2 right-2 absolute"
              onClick={() => {
                setUploadQueue((p) => {
                  if (!p) return p
                  return p.filter((item) => item.id !== preview.id)
                })
              }}
            >
              <Cross2Icon />
            </IconButton>
          </div>
        )
      })}
      <Button
        unstyled
        focusStyled
        className="rounded-lg relative shrink-0 h-[200px] w-[300px] cursor-default hover:bg-dark-shade/50 select-none flex items-center justify-center gap-1 text-sm p-2 !border !border-dashed !border-dark-border-solid"
        onClick={() => {
          inputRef.current?.click()
        }}
        onDragOver={(e) => {
          e.preventDefault()
          setIsDroppingImage(true)
        }}
        onDragLeave={(e) => {
          e.preventDefault()
          setIsDroppingImage(false)
        }}
      >
        <span>
          <PlusIcon />
        </span>
        <span className="font-medium">Add media</span>
        <input
          ref={inputRef}
          className="hidden"
          type="file"
          name="file"
          multiple
          accept={[...VALID_IMAGE_MIME_TYPES, ...VALID_VIDEO_MIME_TYPES].join(
            ','
          )}
          onChange={async (e) => {
            Array.from(e.target.files ?? []).forEach(async (file) => {
              if (
                !file ||
                [...VALID_IMAGE_MIME_TYPES, ...VALID_VIDEO_MIME_TYPES].includes(
                  file.type
                ) === false
              ) {
                e.preventDefault()
                return
              }
              await handleUpload(file)
            })
            e.target.value = ''
            e.target.files = null
          }}
        />

        {isDroppingImage && (
          <div
            className="absolute inset-0 z-50 bg-dark-shade select-none flex items-center justify-center rounded-lg"
            onDrop={(e) => {
              e.preventDefault()
              const file = e.dataTransfer.files?.[0]
              if (
                file &&
                (VALID_IMAGE_MIME_TYPES.includes(file.type) ||
                  VALID_VIDEO_MIME_TYPES.includes(file.type)) &&
                inputRef.current
              ) {
                inputRef.current.files = e.dataTransfer.files
                inputRef.current.dispatchEvent(
                  new Event('change', { bubbles: true })
                )
              }
              setIsDroppingImage(false)
            }}
          >
            Yup, you can drop it here
          </div>
        )}
      </Button>
    </>
  )
}

const Preview = ({ screenshots }: { screenshots: TScreenshot[] }) => {
  return screenshots.map((screenshot, j) => {
    if (VALID_IMAGE_MIME_TYPES.includes(screenshot.mimeType)) {
      return (
        <img
          key={j}
          src={screenshot.url}
          alt={screenshot.alt}
          className="rounded-lg border border-dark-border-solid object-cover w-[480px] h-80"
          style={{
            aspectRatio: `${screenshot.width}/${screenshot.height}`,
          }}
        />
      )
    } else if (VALID_VIDEO_MIME_TYPES.includes(screenshot.mimeType)) {
      return (
        <video
          key={j}
          src={screenshot.url}
          className="rounded-lg border border-dark-border-solid object-cover w-[480px] h-80"
          style={{
            aspectRatio: `${screenshot.width}/${screenshot.height}`,
          }}
          controls
        />
      )
    } else return null
  })
}
